import React, { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { useQueryGetMe } from '@shared/hooks/api/user/use-query-me';

import { useQueryQuestsWithParams } from '@hooks/api/quests/use-query-quests';

import { EmptyState } from '@components/empty-state';
import { Loader } from '@components/loader';
import { Quest } from '@components/quest';
import { StaggerAnimation } from '@components/stagger-animation';

import { config } from '@/config.ts';

import { categoryAll, QuestCategoryFilter, QuestCategoryOption, QuestPagination } from './components';
import * as Styled from './quests-page.styled';

const isSk2 = config.BRAND === 'sk2';

const QuestsPage = () => {
	const { t } = useTranslation('');
	const { data: userData } = useQueryGetMe();
	const isAuthenticated = !!userData?.pm_id;

	const categoryKey = isAuthenticated ? 'quest_category' : 'category';

	const [selectedFilter, setSelectedFilter] = React.useState<QuestCategoryOption>(categoryAll);

	const { data, isLoading, hasNextPage, fetchNextPage } = useQueryQuestsWithParams({
		[categoryKey]: selectedFilter.value
	});

	const categoriesRef = useRef<string[]>(data?.pages?.[0]?.meta?.categories ?? []);

	const items = data?.pages?.flatMap(page => page.data) ?? [];

	const handleSetCategory = (filter: QuestCategoryOption) => {
		if (selectedFilter.value === filter.value) {
			return;
		}
		setSelectedFilter(filter);
	};

	const questsCategories = React.useMemo(() => {
		const categoriesToRender = categoriesRef.current;

		if (categoriesToRender.length === 0) {
			return [];
		}
		return [categoryAll, ...categoriesToRender.map((category: string) => ({ label: category, value: category }))];
	}, [categoriesRef.current]);

	useEffect(() => {
		const categories = data?.pages?.[0]?.meta?.categories ?? [];
		if (categories?.length) {
			categoriesRef.current = categories;
		}
	}, [data]);

	return (
		<Styled.Wrapper>
			{isSk2 && <Styled.Title>{t('quests')}</Styled.Title>}

			<QuestCategoryFilter
				isLoading={isLoading}
				selected={selectedFilter}
				categories={questsCategories}
				setCategory={handleSetCategory}
			/>

			{items.length === 0 && !isLoading && (
				<Styled.Wrapper>
					{isSk2 && <Styled.Title>{t('quests')}</Styled.Title>}
					<EmptyState />
				</Styled.Wrapper>
			)}

			{items.length > 0 && (
				<Styled.QuestsWrapper>
					{items.map((quest, index) => (
						<StaggerAnimation key={quest.id} index={index} delay={50}>
							<Quest quest={quest} />
						</StaggerAnimation>
					))}
				</Styled.QuestsWrapper>
			)}

			{isLoading && <Loader />}

			<QuestPagination hasMore={hasNextPage} isLoading={isLoading} onLoadMore={fetchNextPage} />
		</Styled.Wrapper>
	);
};

export default QuestsPage;
